import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { Button } from '@/components/ui/Button';
import { downloadStoredDocx, saveDocxFile } from '@/lib/patientReports/docxFile';
import { TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';

const DOCX_ACCEPT = '.docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document';

/**
 * Download, replace, or remove a Word template that is already stored.
 * Editing happens in Word. This is not an in-browser editor.
 */
export function WordTemplateFileField({
  inputId,
  storagePath,
  downloadName,
  canEdit,
  busy,
  note,
  className,
  onReplace,
  onRemove,
}: {
  inputId: string;
  storagePath: string | null;
  downloadName: string;
  canEdit: boolean;
  busy: boolean;
  note?: string | null;
  className?: string;
  onReplace: (file: File) => Promise<void>;
  onRemove: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const [file, setFile] = useState<File | null>(null);
  const [inputKey, setInputKey] = useState(0);
  const [downloading, setDownloading] = useState(false);
  const stored = Boolean(storagePath);

  const download = async () => {
    if (!storagePath) return;
    setDownloading(true);
    try {
      const bytes = await downloadStoredDocx(storagePath);
      saveDocxFile(bytes, downloadName);
    } catch {
      toast.error(t('settings.integrations.patient_reports.word_download_error'));
    } finally {
      setDownloading(false);
    }
  };

  const replace = () => {
    if (!file) {
      toast.error(t('settings.integrations.patient_reports.word_required'));
      return;
    }
    void onReplace(file)
      .then(() => {
        setFile(null);
        setInputKey((key) => key + 1);
      })
      .catch(() => undefined);
  };

  if (!stored && !canEdit) return null;

  return (
    <div className={cn('flex flex-col gap-s3', className)}>
      {stored ? (
        <>
          <p className={cn(TYPE.small, 'text-ds-text')} data-testid="word-template-status">
            {t('settings.integrations.patient_reports.word_uploaded')}
          </p>
          <p className={cn(TYPE.small, 'text-ds-muted')}>
            {t('settings.integrations.patient_reports.word_edit_hint')}
          </p>
          {note ? <p className={cn(TYPE.small, 'text-ds-muted')}>{note}</p> : null}
          <div className="flex flex-wrap gap-s2">
            <Button
              type="button"
              variant="secondary"
              loading={downloading}
              disabled={busy}
              onClick={() => void download()}
            >
              {t('settings.integrations.patient_reports.word_download')}
            </Button>
            {canEdit ? (
              <Button
                type="button"
                variant="tertiary"
                loading={busy}
                onClick={() => void onRemove().catch(() => undefined)}
              >
                {t('settings.integrations.patient_reports.word_remove')}
              </Button>
            ) : null}
          </div>
        </>
      ) : null}
      {canEdit ? (
        <>
          <label className={cn(TYPE.small, 'font-medium text-ds-text')} htmlFor={inputId}>
            {t(
              stored
                ? 'settings.integrations.patient_reports.word_file_new'
                : 'settings.integrations.patient_reports.word_file',
            )}
          </label>
          <input
            key={inputKey}
            id={inputId}
            type="file"
            accept={DOCX_ACCEPT}
            disabled={busy}
            className="block w-full text-sm text-ds-text"
            onChange={(event) => setFile(event.target.files?.[0] ?? null)}
          />
          <div className="flex flex-wrap gap-s2">
            <Button type="button" variant="secondary" loading={busy} onClick={replace}>
              {t(
                stored
                  ? 'settings.integrations.patient_reports.word_replace'
                  : 'settings.integrations.patient_reports.word_upload',
              )}
            </Button>
          </div>
        </>
      ) : null}
    </div>
  );
}
